import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import type { StackHealthResponse, HealthSignal, OverallStatus } from '../types/health';

interface UseStackHealthReturn {
  signals: HealthSignal[];
  overall: OverallStatus;
  checkedAt: string;
  source?: 'redis' | 'memory';
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useStackHealth(): UseStackHealthReturn {
  const [signals, setSignals] = useState<HealthSignal[]>([]);
  const [overall, setOverall] = useState<OverallStatus>('nominal');
  const [checkedAt, setCheckedAt] = useState<string>('');
  const [source, setSource] = useState<'redis' | 'memory' | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      const response = await api.get<StackHealthResponse>('/stack-health');
      const data = response.data;
      setSignals(data.signals);
      setOverall(data.overall);
      setCheckedAt(data.checked_at);
      setSource(data.source);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch health status');
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const response = await api.post<StackHealthResponse>('/stack-health/refresh');
      const data = response.data;
      setSignals(data.signals);
      setOverall(data.overall);
      setCheckedAt(data.checked_at);
      setSource(data.source);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh health status');
    }
  }, []);

  // Initial fetch + 30s polling interval
  // No WSS push in v1 — existing WebSocket infra is session/workflow-keyed,
  // no global broadcast channel reaches Home/Admin pages. Honest polling.
  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30_000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  // Re-fetch on visibility change (tab switch back — catches missed updates)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchHealth();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchHealth]);

  return { signals, overall, checkedAt, source, loading, error, refresh };
}
